import * as actions from '../actions/types';
import CMX from '../api';
import { dialogClose, dialogOpen } from '../actions/creators/dialogs';
import { fetchSmartIframeUrl, loyaltySubPaymentPost, ticketsPaymentPost, paymentError, paymentIsProcessing, paymentSuccessWithContext, paymentIsProcessingAsync } from '../actions/creators/checkout/payment';
import { fetchStripeRedirectUrl } from '../actions/creators/checkout/stripe';
import { getCheckoutContext, getCheckoutMovie, getCheckoutNextStep, getCheckoutPaymentGateway, getCheckoutPrevStep, getCheckoutSeatsToSelect, getCheckoutSelectedSeats, getCheckoutSession, getCheckoutStep, getCheckoutTicketsWithQuantity, getCheckoutTransactionId, getUserInfo, isCheckoutTermsChecked, isUserLogged } from '../reducers';
import { updateInfo } from '../actions/creators/user';
import { CHECKOUT_CONTEXT_LOYALTY, checkoutSetStep, displayNoTermsCheckedDialog, timerResume, timerStop } from '../actions/creators/checkout';
import { customLoadingAnimationFetchSuccess } from '../actions/creators/ui';
import { onApiError } from './misc';
import { seatsSelectionSubmit } from '../actions/creators/checkout/seats';
import { ticketSelectionSubmit } from '../actions/creators/checkout/tickets';

export const checkoutMiddleware = ({ getState, dispatch }) => next => async action => {
    pre(action, getState, dispatch);
    next(action);
    post(action, getState, dispatch);
};

function pre(action, getState, dispatch) {
    switch (action.type) {
        case actions.CHECKOUT_ABANDON:
            return onCheckoutAbandonPre(dispatch, getState);
        case actions.CHECKOUT_SEAT_SELECT:
            return onSeatSelect(action, getState, dispatch);
        case actions.AUTH_LOGOUT:
            return localStorage.removeItem('after_purchase_ir_reminder');
    }
}

function post(action, getState, dispatch) {
    switch (action.type) {
        case actions.CHECKOUT_TIMER_TIMEOUT:
            return document.body.classList.remove('js-cursor');
        case actions.CHECKOUT_PAYMENT_SUCCESS:
            return onCheckoutPaymentSuccess(action, getState, dispatch);
        case actions.POST_REGISTER_POPUP_CLOSE:
            return onPostRegisterPopupClose(action, getState, dispatch);
        case actions.POST_PURCHASE_IE_POPUP_SHOW:
            return onPostPurchaseIePopupshow();
        case actions.CHECKOUT_ABANDON:
            return onCheckoutAbandon(dispatch, getState);
        case actions.CHECKOUT_SESSION_FETCH_SUCCESS:
            return onSessionFetchSuccess(action, getState, dispatch);
        case actions.CHECKOUT_PAYMENT_METHOD_SELECT_BEFORE:
            return onPaymentMethodSelect(getState, dispatch, action);
        case actions.CHECKOUT_PAYMENT_FORM_SUBMIT:
            return onPaymentPost(getState, dispatch, action);
        case actions.CHECKOUT_PAYMENT_ERROR:
            return onPaymentError(dispatch, action);
        case actions.CHECKOUT_NEXT_STEP:
            return onCheckoutNextStep(action, getState, dispatch);
        case actions.CHECKOUT_PREV_STEP:
            return onCheckoutPrevStep(action, getState, dispatch);
        case actions.CHECKOUT_PAYMENT_PROCESSING_ASYNC:
            return onPaymentAsyncProcessing(getState, dispatch, action);
        case actions.CHECKOUT_PAYMENT_IS_PROCESSING:
            return dispatch(action.isLoading ? timerStop() : timerResume());
        case actions.STRIPE_CREATE_PAYMENT_INTENT_ERROR:
            return onCreatePaymentIntentError(action, getState, dispatch);
        default:
            return;
    }
}

function onPaymentMethodSelect(getState, dispatch, action) {
    const state = getState();

    if (!isCheckoutTermsChecked(state)) {
        return dispatch(displayNoTermsCheckedDialog());
    }

    if (('mayNeedExternalUrl' in action) && action.mayNeedExternalUrl) {
        const paymentGateway = getCheckoutPaymentGateway(state);
        
        if (paymentGateway === 'smart') {
            dispatch(fetchSmartIframeUrl());
        } else if (paymentGateway === 'stripe' || paymentGateway === 'deuna') {
            dispatch(fetchStripeRedirectUrl());
        }
    }

    dispatch({
        type: actions.CHECKOUT_PAYMENT_METHOD_SELECT,
        method: action.method,
    });
}

function onPaymentPost(getState, dispatch, action) {
    switch (getCheckoutContext(getState())) {
        case CHECKOUT_CONTEXT_LOYALTY:
            return dispatch(loyaltySubPaymentPost(action));
        default:
            return dispatch(ticketsPaymentPost(action));
    }
}

function onPaymentError(dispatch, action) {
    const {error, errorcode} = action;

    dispatch(timerResume());

    if ('pending' === errorcode) {
        dispatch(dialogOpen({
            message: error.message,
            buttons: [
                {
                    label: 'Continuar',
                    primary: true,
                    onClick: (dialog) => {
                        dispatch(dialogClose(dialog.id));
                        setTimeout(() => document.location = '/', 100);
                    }
                },
            ]
        }));

        return;
    }

    // if (errorcode === 'possible-duplicate') {
    //     dispatch(dialogOpen({
    //         message: error.message,
    //         buttons: [
    //             {
    //                 label: 'Cancelar',
    //                 onClick: (dialog) => {
    //                     dispatch(dialogClose(dialog.id));
    //                 }
    //             },
    //             {
    //                 label: 'Confirmar',
    //                 primary: true,
    //                 onClick: (dialog) => {
    //                     dispatch(dialogClose(dialog.id));
    //                     params.repeat_key = error.response.repeat_key;
    //                     dispatch(paymentIsProcessing(true));
    //                     dispatch(actualPaymentPost(params));
    //                 }
    //             }
    //         ]
    //     }));

    //     return;
    // }

    return onApiError(dispatch, Object.assign({context: 'payment'}, action));
}

function onCheckoutPaymentSuccess(action, getState, dispatch) {
    const state = getState();

    dispatch(timerStop());
    
    if (!isUserLogged(state)) return;

    const userInfo = getUserInfo(state);

    if (userInfo.iecode) return;
    if (userInfo.post_purchase_ie_reminder_disabled) return;

    const {payment} = action.data;

    if (!payment) {
        return;
    }

    const amount = (('cash' in payment.methods) ? payment.methods.cash : 0) / 100;
    if (!amount) return;
        
    localStorage.setItem('after_purchase_ir_reminder', JSON.stringify({
        amount
    }));
}

function onPostRegisterPopupClose(action, getState, dispatch) {
    if (action.args && action.args.remember) {
        dispatch(updateInfo({
            post_purchase_ie_reminder_disabled: true
        }));
    }
}

function onPostPurchaseIePopupshow() {
    localStorage.removeItem('after_purchase_ir_reminder');
}

function onCheckoutAbandonPre(dispatch, getState) {
    invalidateTransaction(getState);
}

function onCheckoutAbandon(dispatch, getState) {
    dispatch(timerStop());
}

function invalidateTransaction(getState) {
    const state = getState();
    const transactionId = getCheckoutTransactionId(state);
    const session = getCheckoutSession(state);

    if (transactionId && session) {
        CMX.post('/buy/invalidateTransaction', {
            transaction_id: transactionId,
            session_id: session.id,
        });
    } else {
        console.log('do not invalidate transaction, no transaction id');
    }
}

function onSessionFetchSuccess(action, getState, dispatch) {
    if (!action.data.loading_override) return;
 
    fetch(action.data.loading_override.sprite)
        .then(res => res.json())
        .then(data => dispatch(customLoadingAnimationFetchSuccess({data})));
}

function onSeatSelect(action, getState, dispatch) {
    const state = getState();
    const oldSelection = getCheckoutSelectedSeats(state);
    const seatsToSelect = getCheckoutSeatsToSelect(state);
    const { seat } = action;
        
    if (seat.status !== '0' || oldSelection.indexOf(seat.id) > -1) {
        action.seats = oldSelection;
        return;
    }

    const itemsToRemove = seatsToSelect === oldSelection.length ? 1 : 0;
    const newSelection = oldSelection.slice(itemsToRemove);
    newSelection.push(seat.id);

    action.seats = newSelection;
}

function onCheckoutNextStep(action, getState, dispatch) {
    const state = getState();
    const step = getCheckoutNextStep(state);
    const currentStep = getCheckoutStep(state);

    if ('tickets' === currentStep) {
        const movie = getCheckoutMovie(state);
        const tickets = getCheckoutTicketsWithQuantity(state);
        dispatch(ticketSelectionSubmit(movie, tickets));
        return;
    }

    if ('seats' === currentStep) {
        dispatch(seatsSelectionSubmit());
        return;
    }

    dispatch(checkoutSetStep(step));
}

function onCheckoutPrevStep(action, getState, dispatch) {
    const step = getCheckoutPrevStep(getState());

    dispatch({
        type: actions.CHECKOUT_SET_STEP,
        step
    });
}

function onPaymentAsyncProcessing(getState, dispatch, action) {
    if (!action.processing) {
        return;
    }
    
    const pollingInterval = 5000; // 5 seconda
    const maxPollingTime = 60000; // 60 seconds
    const startTime = Date.now();

    function checkTransactionStatus() {
        if (Date.now() - startTime >= maxPollingTime) {
            timer = null;
            dispatch(paymentIsProcessingAsync({...action, processing: false}));
            dispatch(paymentError({
                errorcode: 'pending-purchase',
                message: 'Tu compra está tardando un poco más de lo habitual en procesarse. En breve recibirás una notificación en tu correo electrónico con el resultado de la misma.',
            }));
            return;
        }
        
        CMX.post('/transactions/temp/getStatus', {
            transactionId: ('transactionId' in action) ? action.transactionId : getCheckoutTransactionId(getState()),
        }).then(res => {
            if (!res.ready) {
                timer = setTimeout(checkTransactionStatus, pollingInterval);
                return;
            }
            
            timer = null;
            
            if (res.result.success) {
                dispatch(paymentSuccessWithContext(action.context, res.result.data));
            } else {
                dispatch(paymentError(res.result, action))
            }
        });
    }
    
    let timer = setTimeout(checkTransactionStatus, pollingInterval);

    dispatch(paymentIsProcessing(true));
}

function onCreatePaymentIntentError(action, getState, dispatch) {
    let message;

    if (action.error && action.error.response && action.error.response.message) {
        message = action.error.response.message;
    }

    if (!message) {
        message = 'Ocurrió un error. Por favor, inténtalo nuevamente.';
    }

    dispatch(dialogOpen({
        message,
        buttons: [
            {
                label: 'Continuar',
                primary: true,
                onClick: (dialog) => {
                    dispatch(dialogClose(dialog.id));
                }
            },
        ]
    }));
}